
export enum SCREEN {
    PROGRAM = "PROGRAM",
    SETUP = "SETUP",
    OUTPUT = "OUTPUT",
}

export enum ProgramStepType {
    HOME = "home",
    SINGLE_POINT = "single_point",
    PATH = "path",
    TRAVEL = "travel",
    ROTATE = "rotate",
    SPINDLE_CMD = "spindle_cmd",
}

export enum RobotTravelType {
    LINEAR = "linear",
    JOINT = "joint",
}

export const programStepTypeDescriptions = {
    [ProgramStepType.HOME]: "call the robot and positioner home program",
    [ProgramStepType.TRAVEL]: "move the robot to a specific location",
    [ProgramStepType.PATH]: "generate a toolpath based on part geometry",
    [ProgramStepType.ROTATE]: "rotate the positioner to a specific angle",
    [ProgramStepType.SPINDLE_CMD]: "send a command to the spindle",
    [ProgramStepType.SINGLE_POINT]: "move the robot to a specific point",
}

export const programStepTypeImages = {
    [ProgramStepType.HOME]: "/assets/icons/home.svg",
    [ProgramStepType.TRAVEL]: "/assets/icons/takeoff.svg",
    [ProgramStepType.PATH]: "/assets/icons/path.svg",
    [ProgramStepType.ROTATE]: "/assets/icons/rotate_flat.svg",
    [ProgramStepType.SPINDLE_CMD]: "/assets/icons/tool_room.svg",
    [ProgramStepType.SINGLE_POINT]: "/assets/icons/point.svg",
}



export interface ProgramStep_Base {
    id: string;
    type: ProgramStepType;
    name: string;
    description: string;
}

export interface ProgramStep_Home extends ProgramStep_Base {
    type: ProgramStepType.HOME;
}

export interface ProgramStep_Travel extends ProgramStep_Base {
    type: ProgramStepType.TRAVEL;
    scenePoint: ScenePoint;
    travelType: RobotTravelType;
    speed: number;
    positionerAngle: number;
}

export interface ProgramStep_Path extends ProgramStep_Base {
    type: ProgramStepType.PATH;
    partPoints: PartPoint[];
    alignmentDistance: number;
    alignmentSpeed: number;
    axialRotation: number;
    positionerAngle: number;
    plungeStartDistance: number;
    plungeSpeed: number;
    plungeApproachSpeed: number;
    retractDistance: number;
    retractSpeed: number;
}

export interface ProgramStep_SinglePoint extends ProgramStep_Base {
    type: ProgramStepType.SINGLE_POINT;
    partPoint: PartPoint;
    alignmentDistance: number;
    alignmentSpeed: number;
    axialRotation: number;
    positionerAngle: number;
    plungeStartDistance: number;
    plungeSpeed: number;
    plungeApproachSpeed: number;
}

export interface ProgramStep_Rotate extends ProgramStep_Base {
    type: ProgramStepType.ROTATE;
    angle: number;
    speed: number;
    waitForCompletion: boolean;
}

export interface ProgramStep_SpindleCmd extends ProgramStep_Base {
    type: ProgramStepType.SPINDLE_CMD;
    state: boolean;
    speed: number;
}

export type ProgramStep = ProgramStep_Home | ProgramStep_Travel | ProgramStep_Path | ProgramStep_Rotate | ProgramStep_SpindleCmd | ProgramStep_SinglePoint;


export enum RobotProgramStatus {
    DRAFT = "draft",
    RELEASED = "released",
}

export interface RobotProgram {
    id: string;
    name: string;
    programGroupId: string;
    steps: ProgramStep[];
    status: RobotProgramStatus;
    versionNumber: number | null;
    releaseNote: string | null;
    releasedOn: Date | null;
    releasedById: string | null;
    updatedOn: Date | null;
    updatedById: string | null;
    downloadUrl: string | null;
    stlDownloadUrl: string | null;
    sceneSetup: RobotProgramSceneSetup;
}

export interface RobotProgramSceneSetup {
    x: number;
    y: number;
    z: number;
    rx: number;
    ry: number;
    rz: number;
    positionerAngle: number;
    offset: number;
}

// defines a point that is fixed on the part
// along with all of the information necessary to choose
// the cutting angle and offset for the toolpath
export interface PartPoint { // new PartPoint model
    id: string;
    position: Position;
    positionNormal: Position;
    orientation: Position;
    normalOffset: number;
    speed: number;
    axialRotation: number;
    positionerAngle: number;
}

// A point and direction vector that is defined in the 
// global coordinate system 
export interface ScenePoint {
    id: string;
    position: Position;
    dirX: number;
    dirY: number;
    dirZ: number;
    axialRotation: number; // degrees
}


// A point that is defined in the coordinate system that the robot uses
// the A, B, and C values are the rotation angles from the fixed robot coordinate system (not Euler angles)
export interface RobotPoint {
    x: number;
    y: number;
    z: number;
    a: number;
    b: number;
    c: number;
}


export interface Position {
    x: number;
    y: number;
    z: number;
}
